import { AsyncPipe } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { Router } from '@angular/router'
import { TranslateModule } from '@ngx-translate/core'
import { setSeconds } from 'date-fns'
import { combineLatest, filter, map, take, tap } from 'rxjs'
import { callResourceTypesToUpload } from '../call-document-upload/call-document-upload.component'
import { CallsService, MinutesStyling, UploadCallData } from '../calls.service'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { getMinuteStylesOfOrganization } from '../organizations.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToastService } from '../toast.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { SelectionComponent } from '../users-selection/selection.component'
import { User, UsersService } from '../users.service'
import { constructNameForUser, DocumentUploadInfo } from '../utils'

@Component({
  selector: 'app-upload-call-page',
  standalone: true,
  imports: [
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    AsyncPipe,
    SpinnerComponent,
    MatDatepickerModule,
    MatIcon,
    SelectionComponent,
    MinutesStylingComponent,
    ToolbarComponent,
    FileUploadComponent,
    DatetimePickerComponent,
  ],
  templateUrl: './upload-call-page.component.html',
  styleUrl: './upload-call-page.component.scss',
})
export class UploadCallPageComponent implements OnInit {
  callsService = inject(CallsService)
  uploadCallMutation = this.callsService.uploadCall()
  toastService = inject(ToastService)
  router = inject(Router)
  usersService = inject(UsersService)
  currentUserQuery$ = this.usersService.getCurrentUser()
  recordingFileTypes = ['mp3', 'mp4', 'wav']

  currentUser$ = this.usersService.currentUser$.pipe(
    take(1),
    tap((user) => {
      if (user === null || this.data !== null) {
        return
      }
      this.data = {
        name: '',
        location: '',
        fileExtension: null,
        uploadKey: null,
        date: setSeconds(new Date(), 0),
        attendees: [],
        userIds: [],
        customDocumentUploadInfo: null,
        agendaUploadInfo: null,
        agendaType: null,
        ...getMinuteStylesOfOrganization(user.organization),
      }
    })
  )

  data: UploadCallData | null = null
  organizationUsers$ = this.usersService.getOrganizationUsers()

  organizationUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.organizationUsers$]).pipe(
    map(([currentUserResult, organizationUsersResult]) => {
      if (currentUserResult.isSuccess && organizationUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = organizationUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  get agendaExtensions(): string[] {
    return callResourceTypesToUpload['AGENDA'].extensions
  }

  get agendaMaxSize(): number | null {
    return callResourceTypesToUpload['AGENDA'].maxSize
  }

  ngOnInit() {
    this.currentUser$.subscribe()

    this.uploadCallMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((call) => {
        this.toastService.show('UPLOAD_CALL.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', call!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  uploadCall() {
    this.uploadCallMutation.mutate(this.data!)
  }

  onRecordingChange(uploadInfo: DocumentUploadInfo | null) {
    if (uploadInfo === null) {
      this.data!.uploadKey = null
      this.data!.fileExtension = null
    } else {
      this.data!.uploadKey = uploadInfo.key
      this.data!.fileExtension = uploadInfo.extension
    }
  }

  onUserSelectionChange(users: User[]) {
    this.data!.userIds = users.map((user) => user.id)
  }

  updateMinutesStyling(minutesStyling: Partial<MinutesStyling>) {
    this.data = { ...this.data!, ...minutesStyling }
  }
}
